/* This component layout is base for subnavigation component so if this layout changes subnavigation should change too! */
import {
    Box,
    Button,
    Container,
    Flex,
    HStack,
    IconButton,
    Stack,
    Text,
    useTheme,
    Link as ChakraLink
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import React, {useEffect, useMemo, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    _360Icon,
    ARIcon,
    ArrowLeftRound2Icon,
    ArrowRightRound2Icon,
    EditIcon,
    PlayIcon,
    ZoomIcon
} from '../../../components/icons'
import ImageGallery from '../../../components/image-gallery'
import ImageGallerySkeleton from '../../../components/image-gallery/skeleton'
import {CRO_EXPERIMENTS, MAIN_IMAGES_IMAGE_TYPE} from '../../../constants'
import useElementScreenPosition from '../../../hooks/use-element-screen-position'
import PdpCta from './pdp-cta'
import CockpitVariations from './cockpit-variations'
import CockpitOrderSection from './cockpit-order-section'
import CockpitTags from './cockpit-tags'
import ProductSpecialOffers, {
    useProductSpecialOffers
} from '../../../components/special-offer/product-special-offers'
import useAR from '../../../hooks/use-ar'
import {EngravingModal, useEngravingModal} from '../../../hooks/use-engraving-modal'
import useMultiSite from '../../../hooks/use-multi-site'
import BVProductRating from '../../../components/bazaarvoice/rating'
import {useCroSlot, useCroVariantActive} from '../../../components/cro'
import {getBVReviewProductIds} from '../../../utils/product-utils'
import {hasBVProductRating} from '../../../components/bazaarvoice/utils'
import WishlistToggle from '../../../components/wishlist-toggle'
import useCockpitGalleryImages from '../hooks/use-cockpit-gallery-images'
import CockpitInformation from './cockpit-information'

const Cockpit = ({
    derivedProduct,
    focusRef,
    product,
    promotions,
    isMasterLoading,
    isPromotionsLoading,
    isVariantLoading,
    showZoomModal,
    onAddToCartClick,
    onHotspotClick,
    onMasterChange,
    onVariantChange,
    onRatingsClick,
    onRecommendedProductClick
}) => {
    const {formatMessage} = useIntl()
    const theme = useTheme()
    const {
        setRef: setHeadingRef,
        position: {top: headingTop},
        recalculatePosition
    } = useElementScreenPosition()
    // recalculate top positions when product changes
    useEffect(() => {
        recalculatePosition()
    }, [product, recalculatePosition])
    const {site} = useMultiSite()

    const {has3D, configurator3DLink, ARModel, videos, hotspots, isEngravable, quantity} =
        derivedProduct

    const isCro63Active = useCroVariantActive(
        CRO_EXPERIMENTS.cro63PdpHideRating.experimentId,
        CRO_EXPERIMENTS.cro63PdpHideRating.variantId
    )

    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])
    const ARProps = useAR(ARModel)
    const engravingModal = useEngravingModal()
    const imagesContainerRef = useRef(null)
    const productSpecialOffers = useProductSpecialOffers(product, undefined, 'pdp')
    const cockpitVariationsRef = useRef(null)

    const mainGalleryImages = useCockpitGalleryImages(product, derivedProduct)
    const mainHotspots = useMemo(() => hotspots?.[MAIN_IMAGES_IMAGE_TYPE], [hotspots])

    const stickyProps = {
        position: {lg: 'sticky'},
        top: {lg: `${headingTop}px`}
    }

    const sidebarSizeProps = {
        // figma is based on 12 column grid
        // this is the rough equivalent in chakra
        flexBasis: {
            lg: 'calc(25% + 0.5rem)',
            xl: 'calc(25% + 0.875rem)',
            '2xl': 'calc(25% + 0.75rem)'
        },
        // prevents children from stretching above the flex-basis size
        minWidth: 0
    }

    const onShowAllColorsClick = (isOpen) => {
        if (imagesContainerRef.current && !isOpen) {
            imagesContainerRef.current.scrollIntoView?.({block: 'start', behavior: 'smooth'})
        }
    }

    useCroSlot(`master_product_${getBVReviewProductIds(product)}`)

    return (
        <>
            <Container layerStyle="page" variant="no-padding">
                {/* Headline */}
                <Flex
                    ref={setHeadingRef}
                    direction={{base: 'column', lg: 'row'}}
                    gap={{base: 6, '2xl': 8}}
                    alignItems={{lg: 'flex-start'}}
                >
                    {/* mobile top / desktop left column */}
                    <Stack spacing={6} px={{base: 4, lg: 0}} {...stickyProps} {...sidebarSizeProps}>
                        {/* Headline */}
                        <Stack spacing={2}>
                            {/* Title */}
                            <Text as={'h1'} textStyle="Regular 2XLarge">
                                {product?.name}
                            </Text>

                            <Flex direction="row" flexWrap="wrap" gap={4}>
                                {hasProductRating && !isCro63Active && (
                                    <BVProductRating
                                        product={product}
                                        variant="cockpit"
                                        onRatingsClick={onRatingsClick}
                                    />
                                )}
                                <CockpitTags product={product} />
                            </Flex>
                        </Stack>

                        <Box display={{base: 'none', lg: 'block'}}>
                            <CockpitInformation
                                product={product}
                                isPromotionsLoading={isPromotionsLoading}
                                productSpecialOffers={productSpecialOffers}
                            />
                        </Box>
                    </Stack>

                    {/* Product image - STOK-6322 */}
                    <Box
                        marginTop={{base: -6, md: 0}}
                        ref={imagesContainerRef}
                        flex={1}
                        {...stickyProps}
                    >
                        {!isVariantLoading ? (
                            <ImageGallery
                                allowAutoplay
                                variant="cockpit"
                                focusRef={focusRef}
                                items={mainGalleryImages}
                                hotspots={mainHotspots}
                                cursor={`url('${encodeURI(
                                    getAssetUrl('static/ico/zoom.svg')
                                )}'),zoom-in`}
                                navLeftIcon={<ArrowLeftRound2Icon boxSize={8} />}
                                navRightIcon={<ArrowRightRound2Icon boxSize={8} />}
                                preload
                                showChildrenIn="overlay"
                                showPagination
                                imageProps={{
                                    widths: {base: '100vw', lg: '50vw'}
                                }}
                                onClick={
                                    showZoomModal &&
                                    ((_, image, index) => {
                                        showZoomModal({
                                            image,
                                            index,
                                            finalFocusRef: _.target
                                        })
                                    })
                                }
                                onHotspotClick={onHotspotClick}
                            >
                                <Box pointerEvents="auto">
                                    <WishlistToggle product={product} variant="imageGallery" />
                                </Box>
                                <Flex
                                    justifyItems="space-between"
                                    position="absolute"
                                    bottom={6}
                                    left={6}
                                    right={6}
                                >
                                    <HStack spacing={2} pointerEvents="auto">
                                        {videos && videos?.length && (
                                            <IconButton
                                                variant="pillOnTile"
                                                aria-label={formatMessage({
                                                    id: 'product_detail.button.assistive_msg.video',
                                                    defaultMessage: 'Video'
                                                })}
                                                onClick={(e) => {
                                                    showZoomModal({
                                                        tab: 1,
                                                        finalFocusRef: e.target
                                                    })
                                                }}
                                            >
                                                <PlayIcon boxSize={6} />
                                            </IconButton>
                                        )}
                                        {ARProps?.hasAR && (
                                            <>
                                                <IconButton
                                                    as={ChakraLink}
                                                    href={ARProps?.href || '#'}
                                                    rel="ar"
                                                    className="fest"
                                                    variant="pillOnTile"
                                                    aria-label={formatMessage({
                                                        id: 'product_detail.button.assistive_msg.ar',
                                                        defaultMessage: 'AR'
                                                    })}
                                                    onClick={ARProps?.onARLinkClick}
                                                >
                                                    <ARIcon boxSize={6} />
                                                </IconButton>
                                            </>
                                        )}
                                        {has3D && (
                                            <IconButton
                                                variant="pillOnTile"
                                                aria-label={formatMessage({
                                                    id: 'product_detail.button.assistive_msg.360view',
                                                    defaultMessage: '360 View'
                                                })}
                                                onClick={(e) => {
                                                    showZoomModal({
                                                        tab: 2,
                                                        finalFocusRef: e.target
                                                    })
                                                }}
                                            >
                                                <_360Icon boxSize={6} />
                                            </IconButton>
                                        )}
                                    </HStack>
                                    <IconButton
                                        pointerEvents="auto"
                                        display={{md: 'none'}}
                                        marginLeft="auto"
                                        variant="pillOnTile"
                                        aria-label={formatMessage({
                                            id: 'product_detail.button.assistive_msg.zoom',
                                            defaultMessage: 'Zoom'
                                        })}
                                        onClick={(e) => {
                                            showZoomModal({tab: 0, finalFocusRef: e.target})
                                        }}
                                    >
                                        <ZoomIcon boxSize={6} />
                                    </IconButton>
                                </Flex>
                            </ImageGallery>
                        ) : (
                            <ImageGallerySkeleton />
                        )}
                    </Box>

                    {/* mobile bottom / desktop right column */}
                    <Stack
                        spacing={6}
                        px={{base: 4, lg: 0}}
                        marginTop={{base: -6, md: 0}}
                        {...stickyProps}
                        {...sidebarSizeProps}
                        // figma design asks to put the box shadow to the whole container,
                        // but that shows it around all edges,
                        // instead apply the shadow only on the top edge
                        _before={{
                            // negate the padding to extend the shadow to the full width of the container
                            marginX: {base: -4, lg: 0},
                            // required to display the shadow
                            height: '1px',
                            // negate the height to not take up space
                            marginBottom: '-1px',
                            content: '""',
                            display: {base: 'block', lg: 'none'},
                            boxShadow: theme.shadows.top
                        }}
                    >
                        {/* Frame 3319 */}
                        <Stack spacing={6}>
                            <CockpitVariations
                                product={product}
                                isMasterLoading={isMasterLoading}
                                isVariantLoading={isVariantLoading}
                                onMasterChange={onMasterChange}
                                onVariantChange={onVariantChange}
                                ARProps={ARProps}
                                configurator3DLink={configurator3DLink}
                                onShowAllColorsClick={onShowAllColorsClick}
                                ref={cockpitVariationsRef}
                            />

                            {!site?.isContentOnlySite && isEngravable && (
                                <Button
                                    rightIcon={<EditIcon boxSize={5} />}
                                    variant="outline"
                                    size="lg"
                                    onClick={engravingModal.onOpen}
                                >
                                    <FormattedMessage
                                        id="product_detail.button.engraving"
                                        defaultMessage="Complimentary Engraving"
                                    />
                                </Button>
                            )}
                        </Stack>

                        {/* Special offer on mobile */}
                        <Box display={{base: 'block', lg: 'none'}}>
                            {!isPromotionsLoading && (
                                <ProductSpecialOffers {...productSpecialOffers} />
                            )}
                        </Box>

                        {/* Bottom */}
                        <CockpitOrderSection
                            derivedProduct={derivedProduct}
                            product={product}
                            promotions={promotions}
                            isPromotionsLoading={isPromotionsLoading}
                            isVariantLoading={isVariantLoading}
                            onAddToCartClick={onAddToCartClick}
                            onRecommendedProductClick={onRecommendedProductClick}
                        />
                    </Stack>
                    <PdpCta
                        isPromotionsLoading={isPromotionsLoading}
                        isVariantLoading={isVariantLoading}
                        product={product}
                        promotions={promotions}
                        quantity={quantity}
                        onClick={onAddToCartClick}
                        onRecommendedProductClick={onRecommendedProductClick}
                    />
                    <Box display={{base: 'block', lg: 'none'}}>
                        <Stack px={{base: 4, lg: 0}} spacing={2}>
                            <CockpitInformation
                                product={product}
                                isPromotionsLoading={isPromotionsLoading}
                                productSpecialOffers={productSpecialOffers}
                            />
                        </Stack>
                    </Box>
                </Flex>
            </Container>
            {isEngravable && (
                <EngravingModal
                    {...engravingModal}
                    isLoading={isPromotionsLoading || isVariantLoading}
                    product={product}
                    promotions={promotions}
                    onRecommendedProductClick={onRecommendedProductClick}
                />
            )}
        </>
    )
}

Cockpit.propTypes = {
    derivedProduct: PropTypes.object,
    focusRef: PropTypes.object,
    product: PropTypes.object,
    promotions: PropTypes.array,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    showZoomModal: PropTypes.func,
    notifyMe: PropTypes.func,
    onAddToCartClick: PropTypes.func,
    onHotspotClick: PropTypes.func,
    onMasterChange: PropTypes.func,
    onRecommendedProductClick: PropTypes.func,
    onRatingsClick: PropTypes.func,
    onVariantChange: PropTypes.func
}

export default Cockpit
